import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AccessoriesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { AccessoryFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Badge } from '../base/badge'

interface Props extends TableProps {
  filters?: AccessoryFilters
  createable?: boolean
  deleteable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, deleteable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AccessoriesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete,
    resourceId,
    setResourceId,
  } = useTable({ pageInfo: data?.accessories.pageInfo })

  const deleteAccessory = (id: string) => {
    if (!deleteable) return

    setResourceId(id)
    setOpenDelete(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AccessoryFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Quantity' }, { name: 'Optional' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.accessories.nodes = dedupe([
              ...previousData.accessories.nodes,
              ...fetchMoreResult.accessories.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.accessories.nodes.map(accessory => (
          {
            id: accessory.id,
            columns: [
              filters.accessoryIdEq ? accessory.assetType.name : accessory.accessory.name,
              accessory.quantity,
              <Badge color={accessory.optional ? 'green' : 'red'}>
                { accessory.optional ? 'Yes' : 'No' }
              </Badge>,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deleteAccessory(accessory.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { resourceId && (<DeleteDialog
        id={resourceId}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[Query]}
        setOpen={setOpenDelete}
        onSuccess={
          () => {
            setOpenDelete(false)
            setResourceId(null)
          }
        }
      />) }
      { filters.assetTypeIdEq && <NewDialog
        defaultValues={{ assetTypeId: filters.assetTypeIdEq }}
        open={openNew}
        setOpen={setOpenNew}
      /> }
    </>
  )
}
